import { defineStore } from 'pinia'
import { usePlayerStore } from '~/stores/usePlayerStore'
import { useInventoryStore } from '~/stores/useInventoryStore'
import { useCombatStore } from '~/stores/useCombatStore'
import type { GameSnapshot, OfflineReport, OfflineReportStatus } from '~/types/game'
import { calculateNeutralDPS } from '~/composables/game'
import { getWildGoldReward } from '~/utils/wildGoldReward'
import { snapshotFarmGoldBonusMult } from '~/utils/snapshotFarmGoldBonus'

const defaultSnapshot: GameSnapshot = {
  snapshotDps: 0,
  snapshotZoneDps: 1,
  snapshotGoldRate: 0,
  snapshotEnemyMaxHp: 0,
  snapshotGoldReward: 0,
}

export const useGameStore = defineStore('game', {
  state: (): GameSnapshot & {
    offlineHistory: OfflineReport[]
    offlineStatus: OfflineReportStatus
    activeOfflineReport: OfflineReport | null
  } => ({
    ...defaultSnapshot,
    offlineHistory: [],
    offlineStatus: 'loading',
    activeOfflineReport: null,
  }),

  actions: {
    setOfflineReportLoading() {
      this.offlineStatus = 'loading'
      this.activeOfflineReport = null
    },

    saveOfflineSnapshot(): void {
      const playerStore = usePlayerStore()
      const inventoryStore = useInventoryStore()
      const combatStore = useCombatStore()
      const neutralDPS = calculateNeutralDPS(inventoryStore.team)
      const teamDps = Math.max(0, Math.floor(combatStore.teamDps || 0))
      const effectiveDps = Math.max(1, neutralDPS, teamDps)
      // Le DPS de zone est estimé via l’ennemi courant (30s), sinon fallback sur le DPS effectif.
      const zoneDPS = combatStore.enemy?.maxHp
        ? Math.max(1, Math.floor(combatStore.enemy.maxHp / 30))
        : Math.max(1, effectiveDps)

      this.snapshotDps = effectiveDps
      this.snapshotZoneDps = zoneDPS
      const currentEnemy = combatStore.enemy
      // Evite les snapshots de gains explosifs (boss / shiny x100) qui faussent l'offline.
      const useCurrentEnemyForGoldSnapshot =
        Boolean(currentEnemy) &&
        (currentEnemy?.maxHp ?? 0) > 0 &&
        !Boolean(currentEnemy?.isBoss) &&
        !Boolean(currentEnemy?.isShiny)
      const farmGoldBonusMult = snapshotFarmGoldBonusMult(playerStore)
      if (useCurrentEnemyForGoldSnapshot && currentEnemy) {
        this.snapshotEnemyMaxHp = Math.max(1, Math.floor(currentEnemy.maxHp))
        this.snapshotGoldReward = Math.max(
          0,
          Math.floor(currentEnemy.goldReward * playerStore.ascensionGoldMult * farmGoldBonusMult)
        )
        this.snapshotGoldRate = Math.max(
          1,
          Math.floor((this.snapshotGoldReward * effectiveDps) / this.snapshotEnemyMaxHp)
        )
      } else {
        this.snapshotEnemyMaxHp = 0
        this.snapshotGoldReward = 0
        this.snapshotGoldRate = Math.max(
          1,
          Math.floor(
            getWildGoldReward(
              playerStore.activeCombatGen,
              playerStore.activeCombatZone,
              playerStore.isFarming ? 5 : playerStore.currentStage,
              false
            ) * playerStore.ascensionRewardMult * playerStore.ascensionGoldMult * farmGoldBonusMult
          )
        )
      }
    },

    closeOfflineReport() {
      this.offlineStatus = 'loading'
      this.activeOfflineReport = null
    },

    setOfflineReportFromServer(payload: {
      report: OfflineReport | null
      history?: OfflineReport[]
      status?: OfflineReportStatus
    }) {
      if (Array.isArray(payload.history)) {
        this.offlineHistory = payload.history.slice(0, 3)
      } else if (payload.report) {
        this.offlineHistory = [payload.report, ...this.offlineHistory].slice(0, 3)
      }
      if (payload.report) {
        this.activeOfflineReport = payload.report
      } else if (!this.activeOfflineReport && this.offlineHistory.length > 0) {
        this.activeOfflineReport = this.offlineHistory[0] ?? null
      }
      if (payload.status) {
        this.offlineStatus = payload.status
      } else if (this.activeOfflineReport) {
        this.offlineStatus = 'claimable'
      } else {
        this.offlineStatus = 'cooldown'
      }
    },

    markOfflineReportSeen() {
      this.offlineStatus = 'cooldown'
      this.activeOfflineReport = null
    },
  },
})
